import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, FileText, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import Papa from "papaparse";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// ImportPatientsDialog — V1 (frontend-only)
//
// Scope: name + phone + email only. The create_patient RPC doesn't
// expose date_of_birth / address yet, so those columns are noted as
// out-of-scope in the dialog and can be filled in per-patient after
// import. V2 can add a bulk RPC + DOB/address support.
//
// Flow:
//   1. Pick file
//   2. Parse + auto-map columns (Name/Phone/Email by header heuristics)
//   3. Show preview + per-row validation errors
//   4. Loop create_patient with a progress bar
//   5. Show summary (imported / skipped / failed)
// =====================================================================

const FIELD_OPTIONS = [
  { value: "skip",       label: "— Skip this column —" },
  { value: "full_name",  label: "Full name" },
  { value: "first_name", label: "First name" },
  { value: "last_name",  label: "Last name" },
  { value: "phone",      label: "Phone" },
  { value: "email",      label: "Email" },
];

// Heuristics for auto-mapping CSV headers to patient fields.
const HEADER_HINTS = {
  full_name:  [/^name$/i, /^full[\s_-]?name$/i, /^patient[\s_-]?name$/i, /first.*last/i],
  first_name: [/^first$/i, /^first[\s_-]?name$/i, /^given[\s_-]?name$/i, /^forename$/i],
  last_name:  [/^last$/i, /^last[\s_-]?name$/i, /^surname$/i, /^family[\s_-]?name$/i],
  phone:     [/^phone$/i, /mobile/i, /^cell$/i, /^tel$/i, /contact[\s_-]?number/i],
  email:     [/^e[-_]?mail$/i, /email/i],
};

function autoMapHeaders(headers) {
  const mapping = {};
  const used = new Set();

  const directNameHit = headers.findIndex(
    (h, i) => !used.has(i) && HEADER_HINTS.full_name.some((re) => re.test(h ?? "")),
  );
  if (directNameHit >= 0) {
    mapping[directNameHit] = "full_name";
    used.add(directNameHit);
  } else {
    for (const field of ["first_name", "last_name"]) {
      const hit = headers.findIndex(
        (h, i) => !used.has(i) && HEADER_HINTS[field].some((re) => re.test(h ?? "")),
      );
      if (hit >= 0) {
        mapping[hit] = field;
        used.add(hit);
      }
    }
  }

  for (const field of ["phone", "email"]) {
    const hit = headers.findIndex(
      (h, i) => !used.has(i) && HEADER_HINTS[field].some((re) => re.test(h ?? "")),
    );
    if (hit >= 0) {
      mapping[hit] = field;
      used.add(hit);
    }
  }
  // Anything else defaults to "skip".
  for (let i = 0; i < headers.length; i++) {
    if (mapping[i] === undefined) mapping[i] = "skip";
  }
  return mapping;
}

function mappedValue(row, mapping, field) {
  const entry = Object.entries(mapping).find(([, value]) => value === field);
  if (!entry) return "";
  return (row[entry[0]] ?? "").toString().trim();
}

function combineNameParts(...parts) {
  return parts
    .map((part) => (part ?? "").toString().trim().replace(/\s+/g, " "))
    .filter(Boolean)
    .join(" ");
}

// Validate one row against the mapped fields. Returns { ok, error, payload }.
function validateRow(row, mapping) {
  const payload = {};

  const directFullName = mappedValue(row, mapping, "full_name");
  const splitFullName = combineNameParts(
    mappedValue(row, mapping, "first_name"),
    mappedValue(row, mapping, "last_name"),
  );
  const fullName = directFullName || splitFullName;
  const phone = mappedValue(row, mapping, "phone");
  const email = mappedValue(row, mapping, "email");

  if (fullName) payload.full_name = fullName;
  if (phone) payload.phone = phone;
  if (email) payload.email = email;

  if (!payload.full_name) return { ok: false, error: "Missing name" };
  if (!payload.phone)    return { ok: false, error: "Missing phone" };
  if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return { ok: false, error: "Invalid email" };
  }
  return { ok: true, payload };
}

export default function ImportPatientsDialog({ open, onOpenChange, existingPhones = [], onImported }) {
  const [step, setStep]         = useState("pick");      // 'pick' | 'preview' | 'importing' | 'done'
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders]   = useState([]);
  const [rows, setRows]         = useState([]);          // raw rows: array of arrays
  const [mapping, setMapping]   = useState({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults]   = useState({ imported: [], skipped: [], failed: [] });
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setStep("pick");
    setFileName("");
    setHeaders([]);
    setRows([]);
    setMapping({});
    setProgress({ done: 0, total: 0 });
    setResults({ imported: [], skipped: [], failed: [] });
  }, []);

  const close = () => {
    if (step === "importing") return;        // Don't close mid-import.
    onOpenChange(false);
    setTimeout(reset, 200);                  // Let the dialog finish closing.
  };

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please pick a .csv file.");
      return;
    }
    setFileName(file.name);
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (res) => {
        const data = res.data || [];
        if (data.length < 2) {
          toast.error("CSV needs a header row and at least one data row.");
          return;
        }
        const hdrs = data[0].map((h) => String(h ?? "").trim());
        const body = data.slice(1);
        setHeaders(hdrs);
        setRows(body);
        setMapping(autoMapHeaders(hdrs));
        setStep("preview");
      },
      error: (err) => {
        toast.error(`Could not parse CSV: ${err.message}`);
      },
    });
  }, []);

  // Validation summary for the preview step.
  const validation = useMemo(() => {
    if (step !== "preview") return null;
    const errors = [];
    let valid = 0;
    rows.forEach((row, idx) => {
      const v = validateRow(row, mapping);
      if (v.ok) valid += 1;
      else errors.push({ rowIdx: idx, error: v.error });
    });
    const mappedFields = Object.values(mapping);
    const hasName = mappedFields.includes("full_name")
      || mappedFields.includes("first_name")
      || mappedFields.includes("last_name");
    const hasPhone = Object.values(mapping).includes("phone");
    const mappingComplete = hasName && hasPhone;
    return { valid, errors, mappingComplete };
  }, [step, rows, mapping]);

  async function startImport() {
    if (!validation?.mappingComplete) {
      toast.error("Map a name column and a Phone column first.");
      return;
    }
    if (validation.valid === 0) {
      toast.error("No valid rows to import.");
      return;
    }

    setStep("importing");
    setProgress({ done: 0, total: rows.length });
    const imported = [];
    const skipped  = [];
    const failed   = [];
    const seenPhones = new Set();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const v = validateRow(row, mapping);
      if (!v.ok) {
        skipped.push({ rowIdx: i, reason: v.error });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      // Skip duplicates within this CSV (same phone twice in the file).
      if (seenPhones.has(v.payload.phone)) {
        skipped.push({ rowIdx: i, reason: "Duplicate phone in CSV" });
        setProgress({ done: i + 1, total: rows.length });
        continue;
      }
      seenPhones.add(v.payload.phone);

      const { data, error } = await supabase.rpc("create_patient", { p_data: v.payload });
      if (error) {
        failed.push({ rowIdx: i, name: v.payload.full_name, error: error.message ?? "Unknown error" });
      } else {
        imported.push({ rowIdx: i, name: v.payload.full_name, patient_number: data?.patient_number });
      }
      setProgress({ done: i + 1, total: rows.length });
    }

    setResults({ imported, skipped, failed });
    setStep("done");
    if (imported.length > 0) onImported?.(imported.length);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="import-patients-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-bold text-slate-900">Import patients from CSV</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {step === "pick"      && "V1 imports name, phone, and email. DOB / address can be added later per patient."}
              {step === "preview"   && `${rows.length} row${rows.length === 1 ? "" : "s"} from ${fileName}`}
              {step === "importing" && `Importing… ${progress.done} of ${progress.total}`}
              {step === "done"      && "Import finished."}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={step === "importing"}
            data-testid="import-patients-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {step === "pick"      && <PickStep onPickFile={handleFile} fileInputRef={fileInputRef} />}
          {step === "preview"   && (
            <PreviewStep
              headers={headers}
              rows={rows}
              mapping={mapping}
              setMapping={setMapping}
              validation={validation}
              existingPhones={existingPhones}
            />
          )}
          {step === "importing" && <ImportingStep progress={progress} />}
          {step === "done"      && <DoneStep results={results} />}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
          {step === "preview" ? (
            <>
              <button
                type="button"
                onClick={reset}
                data-testid="import-patients-back"
                className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
              >
                Pick a different file
              </button>
              <button
                type="button"
                onClick={startImport}
                disabled={!validation?.mappingComplete || validation.valid === 0}
                data-testid="import-patients-start"
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50"
              >
                Import {validation?.valid ?? 0} patient{validation?.valid === 1 ? "" : "s"}
              </button>
            </>
          ) : step === "done" ? (
            <button
              type="button"
              onClick={close}
              data-testid="import-patients-done"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm"
            >
              Close
            </button>
          ) : step === "pick" ? (
            <button
              type="button"
              onClick={close}
              data-testid="import-patients-cancel"
              className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Step 1 — Pick file
// ---------------------------------------------------------------------
function PickStep({ onPickFile, fileInputRef }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer?.files?.[0];
          if (f) onPickFile(f);
        }}
        className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${dragOver ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-slate-50"}`}
        data-testid="import-patients-dropzone"
      >
        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
        <p className="text-sm font-semibold text-slate-700">Drop a CSV file here, or</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => onPickFile(e.target.files?.[0])}
          data-testid="import-patients-file-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          data-testid="import-patients-pick-button"
          className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          <FileText className="w-4 h-4" /> Choose file…
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">CSV format tips</p>
          <ul className="mt-1 space-y-0.5 list-disc pl-4">
            <li>First row should be column headers (e.g. <code className="bg-white px-1 rounded">Name</code>, <code className="bg-white px-1 rounded">Phone</code>, <code className="bg-white px-1 rounded">Email</code>).</li>
            <li>Each patient = one row. Name and phone are required.</li>
            <li>Headers are auto-detected; you can adjust the mapping in the next step.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Step 2 — Preview + column mapping
// ---------------------------------------------------------------------
function PreviewStep({ headers, rows, mapping, setMapping, validation, existingPhones }) {
  const previewRows = rows.slice(0, 10);
  return (
    <div className="space-y-4">
      {/* Mapping table */}
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-2">Column mapping</p>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-200">
                    {h || <span className="text-slate-400 italic">column {i + 1}</span>}
                  </th>
                ))}
              </tr>
              <tr>
                {headers.map((_, i) => (
                  <th key={i} className="px-3 py-2 border-b border-slate-200">
                    <select
                      value={mapping[i]}
                      onChange={(e) => setMapping({ ...mapping, [i]: e.target.value })}
                      data-testid={`import-patients-map-${i}`}
                      className="w-full px-2 py-1 text-xs rounded border border-slate-200 bg-white"
                    >
                      {FIELD_OPTIONS.map((o) => {
                        const usedElsewhere = o.value !== "skip"
                          && Object.entries(mapping).some(([k, v]) => Number(k) !== i && v === o.value);
                        return (
                          <option key={o.value} value={o.value} disabled={usedElsewhere}>
                            {o.label}{usedElsewhere ? " (used)" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, rIdx) => (
                <tr key={rIdx} className="even:bg-slate-50/50">
                  {headers.map((_, cIdx) => (
                    <td key={cIdx} className="px-3 py-1.5 text-slate-600 border-b border-slate-100">
                      {String(row[cIdx] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > previewRows.length ? (
          <p className="text-[11px] text-slate-500 mt-1">
            Showing first {previewRows.length} of {rows.length} rows.
          </p>
        ) : null}
      </div>

      {/* Validation summary */}
      {!validation?.mappingComplete ? (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-800 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>Map at least one name column and one <strong>Phone</strong> column to continue.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          <Pill tone="emerald" label="Ready to import" value={validation.valid} />
          <Pill tone="amber"   label="Will be skipped"  value={validation.errors.length} />
          <Pill tone="slate"   label="Phone matches existing" value={countDupes(rows, mapping, existingPhones)} />
        </div>
      )}

      {/* Errors list */}
      {validation?.errors?.length > 0 ? (
        <details className="text-xs">
          <summary className="cursor-pointer font-semibold text-slate-700">
            {validation.errors.length} row{validation.errors.length === 1 ? "" : "s"} will be skipped
          </summary>
          <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto pl-2">
            {validation.errors.slice(0, 50).map((e, i) => (
              <li key={i} className="text-slate-600">
                Row {e.rowIdx + 2}: <span className="text-rose-600">{e.error}</span>
              </li>
            ))}
            {validation.errors.length > 50 ? (
              <li className="text-slate-400 italic">… and {validation.errors.length - 50} more</li>
            ) : null}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function countDupes(rows, mapping, existingPhones) {
  if (!existingPhones?.length) return 0;
  const phoneCol = Object.entries(mapping).find(([, v]) => v === "phone")?.[0];
  if (phoneCol == null) return 0;
  const set = new Set(existingPhones);
  let n = 0;
  for (const r of rows) {
    const p = (r[phoneCol] ?? "").toString().trim();
    if (p && set.has(p)) n += 1;
  }
  return n;
}

function Pill({ tone, label, value }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[11px] font-medium opacity-80">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Step 3 — Importing (progress)
// ---------------------------------------------------------------------
function ImportingStep({ progress }) {
  const pct = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
  return (
    <div className="text-center py-10">
      <Loader2 className="w-8 h-8 text-teal-600 mx-auto mb-3 animate-spin" />
      <p className="text-sm font-semibold text-slate-900">
        Importing patient {progress.done} of {progress.total}…
      </p>
      <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden max-w-md mx-auto">
        <div className="h-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Don't close this window.</p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Step 4 — Done (summary)
// ---------------------------------------------------------------------
function DoneStep({ results }) {
  return (
    <div className="space-y-4">
      <div className="text-center py-4">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
        <p className="text-base font-bold text-slate-900">Import finished</p>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <Pill tone="emerald" label="Imported"  value={results.imported.length} />
        <Pill tone="amber"   label="Skipped"   value={results.skipped.length} />
        <Pill tone="slate"   label="Failed"    value={results.failed.length} />
      </div>

      {results.failed.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
            {results.failed.length} failure{results.failed.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto pl-2 text-xs">
            {results.failed.map((f, i) => (
              <li key={i} className="text-slate-600">
                Row {f.rowIdx + 2} ({f.name}): <span className="text-rose-600">{f.error}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
